import axios from "axios";
import { plainToInstance } from "class-transformer";
import { DataRetrievalFailureReason, DocumentRetrievalTaskBatchDto } from "healthex-common/document.retrieval.dtos";
import { HealthWalletSourceDetailDto, HealthWalletSourceRecordDto, HealthWalletSourcesResponseDto, } from "healthex-common/health.wallet.dtos";
import { err } from "healthex-common/result";
import { asEnum } from "healthex-common/utils";
import { authenticatedApiConfig, generateURL } from "./BaseApiService";
export class HealthWalletService {
    constructor(authenticationProvider, environment) {
        this.authenticationProvider = authenticationProvider;
        this.environment = environment;
    }
    async getTimeline(patientId, params = {}) {
        const response = await axios.get(generateURL(this.environment.API_BASE_URL, "/patients/:patientId/health-wallet/timeline", { patientId }), {
            ...(await authenticatedApiConfig(this.authenticationProvider, params)),
            paramsSerializer: { indexes: null },
        });
        return response.data;
    }
    async getSources(patientId) {
        const response = await axios.get(generateURL(this.environment.API_BASE_URL, "/patients/:patientId/health-wallet/sources", { patientId }), await authenticatedApiConfig(this.authenticationProvider));
        return plainToInstance(HealthWalletSourcesResponseDto, response.data);
    }
    async hideSource(patientId, sourceId) {
        try {
            const response = await axios.post(generateURL(this.environment.API_BASE_URL, "/patients/:patientId/health-wallet/sources/:sourceId/hide", {
                patientId,
                sourceId,
            }), {}, await authenticatedApiConfig(this.authenticationProvider));
            return this.decodeDocumentRetrievalTaskBatchResult(response.data);
        }
        catch (e) {
            return this.checkThrownError(e);
        }
    }
    async unhideSource(patientId, sourceId) {
        try {
            const response = await axios.post(generateURL(this.environment.API_BASE_URL, "/patients/:patientId/health-wallet/sources/:sourceId/unhide", {
                patientId,
                sourceId,
            }), {}, await authenticatedApiConfig(this.authenticationProvider));
            return this.decodeDocumentRetrievalTaskBatchResult(response.data);
        }
        catch (e) {
            return this.checkThrownError(e);
        }
    }
    async getSourceDetail(patientId, sourceId) {
        const response = await axios.get(generateURL(this.environment.API_BASE_URL, "/patients/:patientId/health-wallet/sources/:sourceId", {
            patientId,
            sourceId,
        }), await authenticatedApiConfig(this.authenticationProvider));
        return plainToInstance(HealthWalletSourceDetailDto, response.data);
    }
    async getSourceRecords(patientId, sourceId, kind) {
        const response = await axios.get(generateURL(this.environment.API_BASE_URL, "/patients/:patientId/health-wallet/sources/:sourceId/records", {
            patientId,
            sourceId,
        }), await authenticatedApiConfig(this.authenticationProvider, { kind }));
        return plainToInstance(HealthWalletSourceRecordDto, response.data);
    }
    decodeDocumentRetrievalTaskBatchResult(responseData) {
        if (responseData === null || responseData === void 0 ? void 0 : responseData.error) {
            const failureReason = asEnum(DataRetrievalFailureReason)(responseData.error);
            return err(failureReason !== null && failureReason !== void 0 ? failureReason : DataRetrievalFailureReason.GENERIC_ERROR);
        }
        return responseData ? plainToInstance(DocumentRetrievalTaskBatchDto, responseData) : err(DataRetrievalFailureReason.GENERIC_ERROR);
    }
    checkThrownError(e) {
        var _a;
        if (axios.isAxiosError(e)) {
            if ((_a = e.response) === null || _a === void 0 ? void 0 : _a.data) {
                const errorData = e.response.data;
                for (const candidate of [errorData.code, errorData.message]) {
                    if (typeof candidate === "string") {
                        const failureReason = asEnum(DataRetrievalFailureReason)(candidate);
                        if (failureReason)
                            return err(failureReason);
                    }
                }
            }
        }
        throw e;
    }
    async dismissGap(patientId, body) {
        await axios.post(generateURL(this.environment.API_BASE_URL, "/patients/:patientId/health-wallet/gaps/dismiss", { patientId }), body, await authenticatedApiConfig(this.authenticationProvider));
    }
    async getDetails(patientId, recordType, resourceId, language, unitSystem) {
        const response = await axios.get(generateURL(this.environment.API_BASE_URL, "/patients/:patientId/health-wallet/:recordType/:resourceId", {
            patientId,
            recordType,
            resourceId,
        }), await authenticatedApiConfig(this.authenticationProvider, { unitSystem, language }));
        return response.data;
    }
    async getVaccinationInsight(patientId, resourceId, language) {
        const response = await axios.get(generateURL(this.environment.API_BASE_URL, "/patients/:patientId/health-wallet/vaccination-insights/:resourceId", {
            patientId,
            resourceId,
        }), await authenticatedApiConfig(this.authenticationProvider, { language }));
        return response.data;
    }
    async getMeasurementInsight(patientId, observationId, language, unitSystem) {
        const response = await axios.get(generateURL(this.environment.API_BASE_URL, "/patients/:patientId/health-wallet/measurement-insights/:observationId", {
            patientId,
            observationId,
        }), await authenticatedApiConfig(this.authenticationProvider, { unitSystem, language }));
        return response.data;
    }
    async downloadDocument(patientId, documentId) {
        const response = await axios.get(generateURL(this.environment.API_BASE_URL, "/patients/:patientId/health-wallet/documents/:documentId/download", {
            patientId,
            documentId,
        }), {
            ...(await authenticatedApiConfig(this.authenticationProvider)),
            responseType: "blob",
        });
        return response.data;
    }
}
